import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { useToast } from '../../hooks/use-toast';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import {
  Brain,
  TrendingUp,
  TrendingDown,
  AlertCircle,
  CheckCircle,
  Lightbulb,
  Target,
  Users,
  BarChart3,
  Sparkles,
  MessageSquare,
  ThumbsUp,
  ThumbsDown,
  Meh,
  RefreshCw,
  Download,
  Filter
} from 'lucide-react';
import { 
  VictoryChart, 
  VictoryBar, 
  VictoryAxis, 
  VictoryTheme,
  VictoryPie,
  VictoryLabel,
  VictoryLine,
  VictoryScatter,
  VictoryTooltip
} from 'victory';

const API = process.env.REACT_APP_BACKEND_URL;

const PainelIA180 = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [filtroUsuario, setFiltroUsuario] = useState('');
  const [filtroCiclo, setFiltroCiclo] = useState('');
  
  // Estados para dados de IA
  const [analiseSentimento, setAnaliseSentimento] = useState(null);
  const [recomendacoes, setRecomendacoes] = useState([]);
  const [ciclos, setCiclos] = useState([]);
  const [funcionarios, setFuncionarios] = useState([]);

  useEffect(() => {
    carregarDadosIniciais();
  }, []);

  useEffect(() => {
    if (filtroCiclo || filtroUsuario) {
      carregarAnalisesIA();
    }
  }, [filtroCiclo, filtroUsuario]);

  const carregarDadosIniciais = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Carregar ciclos
      const ciclosRes = await axios.get(`${API}/api/feedback180/ciclos`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCiclos(ciclosRes.data || []);
      
      // Carregar funcionários
      const funcRes = await axios.get(`${API}/api/employees`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFuncionarios(funcRes.data || []);
    } catch (error) {
      console.error('Erro ao carregar dados iniciais:', error);
    }
  };

  const carregarAnalisesIA = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Análise de Sentimento
      const params = {};
      if (filtroCiclo) params.ciclo_id = filtroCiclo;
      if (filtroUsuario) params.usuario_id = filtroUsuario;
      
      const sentimentoRes = await axios.post(
        `${API}/api/feedback180/ia/analise-sentimento`,
        params,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAnaliseSentimento(sentimentoRes.data);
      
      // Recomendações Inteligentes
      const recomendacoesRes = await axios.post(
        `${API}/api/feedback180/ia/recomendacoes`,
        params,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRecomendacoes(recomendacoesRes.data.recomendacoes || []);
      
      toast({
        title: 'Sucesso',
        description: 'Análises de IA carregadas com sucesso',
      });
    } catch (error) {
      console.error('Erro ao carregar análises de IA:', error);
      toast({
        title: 'Erro',
        description: 'Erro ao carregar análises de IA',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const getSentimentoIcon = (sentimento) => {
    switch (sentimento) {
      case 'positivo':
        return <ThumbsUp className="h-5 w-5 text-green-600" />;
      case 'negativo':
        return <ThumbsDown className="h-5 w-5 text-red-600" />;
      case 'neutro':
        return <Meh className="h-5 w-5 text-yellow-600" />;
      default:
        return <MessageSquare className="h-5 w-5 text-gray-600" />;
    }
  };

  const getSentimentoBadge = (sentimento) => {
    switch (sentimento) {
      case 'positivo':
        return <Badge className="bg-green-500 text-white">Positivo</Badge>;
      case 'negativo':
        return <Badge className="bg-red-500 text-white">Negativo</Badge>;
      case 'neutro':
        return <Badge className="bg-yellow-500 text-white">Neutro</Badge>;
      default:
        return <Badge variant="outline">Não analisado</Badge>;
    }
  };

  const getPrioridadeRecomendacao = (prioridade) => {
    switch (prioridade) {
      case 'alta':
        return <Badge variant="destructive">Alta Prioridade</Badge>;
      case 'media':
        return <Badge className="bg-orange-500 text-white">Média Prioridade</Badge>;
      case 'baixa':
        return <Badge variant="secondary">Baixa Prioridade</Badge>;
      default:
        return <Badge variant="outline">Normal</Badge>;
    }
  };

  const exportarRelatorioIA = () => {
    toast({
      title: 'Em breve',
      description: 'Funcionalidade de exportação em desenvolvimento',
    });
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center">
            <Brain className="h-8 w-8 mr-3 text-purple-600" />
            Painel de IA - Feedback 180º
          </h1>
          <p className="text-gray-600 mt-1">
            Análises inteligentes de sentimento e recomendações personalizadas
          </p>
        </div>
        
        <div className="flex space-x-2">
          <Button
            onClick={carregarAnalisesIA}
            disabled={loading}
            variant="outline"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Atualizar
          </Button>
          
          <Button onClick={exportarRelatorioIA} className="bg-purple-600 hover:bg-purple-700">
            <Download className="h-4 w-4 mr-2" />
            Exportar Relatório
          </Button>
        </div>
      </div>

      {/* Filtros */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Filter className="h-5 w-5 mr-2" />
            Filtros de Análise
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Ciclo de Avaliação
              </label>
              <select
                value={filtroCiclo}
                onChange={(e) => setFiltroCiclo(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
              >
                <option value="">Todos os ciclos</option>
                {ciclos.map(ciclo => (
                  <option key={ciclo.id} value={ciclo.id}>
                    {ciclo.nome} ({ciclo.status})
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Funcionário
              </label>
              <select
                value={filtroUsuario}
                onChange={(e) => setFiltroUsuario(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
              >
                <option value="">Todos os funcionários</option>
                {funcionarios.map(func => (
                  <option key={func.id} value={func.id}>
                    {func.nome}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Análise de Sentimento */}
      {analiseSentimento && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Sparkles className="h-5 w-5 mr-2 text-purple-600" />
              Análise de Sentimento
            </CardTitle>
            <CardDescription>
              Análise automática do tom e sentimento das avaliações
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              {/* Card Sentimento Geral */}
              <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-6 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">Sentimento Geral</span>
                  {getSentimentoIcon(analiseSentimento.sentimento_geral)}
                </div>
                <div className="text-3xl font-bold text-gray-900 mb-2">
                  {getSentimentoBadge(analiseSentimento.sentimento_geral)}
                </div>
                <p className="text-sm text-gray-600">
                  Score: {(analiseSentimento.score_sentimento * 100).toFixed(1)}%
                </p>
              </div>

              {/* Card Temas Positivos */}
              <div className="bg-gradient-to-br from-green-50 to-green-100 p-6 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">Temas Positivos</span>
                  <ThumbsUp className="h-5 w-5 text-green-600" />
                </div>
                <div className="text-3xl font-bold text-green-700 mb-2">
                  {analiseSentimento.temas_positivos?.length || 0}
                </div>
                <p className="text-sm text-gray-600">
                  {analiseSentimento.temas_positivos?.slice(0, 2).join(', ') || 'Nenhum tema identificado'}
                </p>
              </div>

              {/* Card Pontos de Atenção */}
              <div className="bg-gradient-to-br from-red-50 to-red-100 p-6 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">Pontos de Atenção</span>
                  <AlertCircle className="h-5 w-5 text-red-600" />
                </div>
                <div className="text-3xl font-bold text-red-700 mb-2">
                  {analiseSentimento.pontos_atencao?.length || 0}
                </div>
                <p className="text-sm text-gray-600">
                  {analiseSentimento.pontos_atencao?.slice(0, 2).join(', ') || 'Nenhum ponto crítico'}
                </p>
              </div>
            </div>

            {/* Detalhes da Análise */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                <MessageSquare className="h-4 w-4 mr-2" />
                Resumo da Análise
              </h4>
              <p className="text-gray-700 leading-relaxed">
                {analiseSentimento.resumo || 'Análise de sentimento em processamento...'}
              </p>
            </div>

            {/* Gráfico de Distribuição de Sentimentos */}
            {analiseSentimento.distribuicao_sentimentos && (
              <div className="mt-6">
                <h4 className="font-semibold text-gray-900 mb-4">Distribuição de Sentimentos</h4>
                <VictoryPie
                  data={[
                    { x: 'Positivo', y: analiseSentimento.distribuicao_sentimentos.positivo || 0 },
                    { x: 'Neutro', y: analiseSentimento.distribuicao_sentimentos.neutro || 0 },
                    { x: 'Negativo', y: analiseSentimento.distribuicao_sentimentos.negativo || 0 }
                  ]}
                  colorScale={['#10b981', '#f59e0b', '#ef4444']}
                  labelRadius={60}
                  style={{ labels: { fontSize: 14, fill: 'white', fontWeight: 'bold' } }}
                  height={250}
                />
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Recomendações Inteligentes */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Lightbulb className="h-5 w-5 mr-2 text-yellow-600" />
            Recomendações Inteligentes
          </CardTitle>
          <CardDescription>
            Sugestões personalizadas geradas por IA para desenvolvimento
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
              <p className="text-gray-600 mt-4">Gerando recomendações inteligentes...</p>
            </div>
          ) : recomendacoes.length > 0 ? (
            <div className="space-y-4">
              {recomendacoes.map((rec, index) => (
                <div 
                  key={index}
                  className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      <Target className="h-5 w-5 text-purple-600" />
                      <h4 className="font-semibold text-gray-900">{rec.titulo}</h4>
                    </div>
                    {getPrioridadeRecomendacao(rec.prioridade)}
                  </div>
                  
                  <p className="text-gray-700 mb-3 leading-relaxed">
                    {rec.descricao}
                  </p>
                  
                  {rec.acoes_sugeridas && rec.acoes_sugeridas.length > 0 && (
                    <div className="bg-blue-50 p-3 rounded-lg">
                      <p className="text-sm font-medium text-blue-900 mb-2">Ações Sugeridas:</p>
                      <ul className="space-y-1">
                        {rec.acoes_sugeridas.map((acao, idx) => (
                          <li key={idx} className="text-sm text-blue-800 flex items-start">
                            <CheckCircle className="h-4 w-4 mr-2 mt-0.5 flex-shrink-0" />
                            {acao}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {rec.impacto_esperado && (
                    <div className="mt-3 flex items-center text-sm text-gray-600">
                      <TrendingUp className="h-4 w-4 mr-1 text-green-600" />
                      Impacto esperado: {rec.impacto_esperado}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <Brain className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-600">Selecione um ciclo ou funcionário para gerar recomendações</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Estatísticas de IA */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Total de Análises</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-purple-600">
              {analiseSentimento ? '1' : '0'}
            </div>
            <p className="text-xs text-gray-600 mt-1">Análises realizadas</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Recomendações Geradas</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-yellow-600">
              {recomendacoes.length}
            </div>
            <p className="text-xs text-gray-600 mt-1">Sugestões de IA</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Acurácia do Modelo</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-green-600">
              94.5%
            </div>
            <p className="text-xs text-gray-600 mt-1">Precisão da IA</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PainelIA180;
